import { useCallback, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import type { TopBannerContentType } from '@/graphql/types';
import Button from '../form/Button';

function hideBanner(
    cookieName: string,
    cookieTtlDays: number,
    setShowBanner: (value: boolean) => void
): void {
    Cookies.set(cookieName, '1', { expires: cookieTtlDays });
    setShowBanner(false);
}

export interface TopBannerProps extends TopBannerContentType {
    cookieName: string;
    cookieTtlDays: number;
}

function TopBanner({
    prefixText,
    items,
    cta,
    cookieName,
    cookieTtlDays,
}: TopBannerProps): JSX.Element | null {
    const [showBanner, setShowBanner] = useState(!(Cookies.get(cookieName) === '1'));
    const hasPrefixText = Boolean(prefixText);
    const hasItems = items.length > 0;

    const itemRows = useMemo(() => {
        // Group items into rows of 2 so the banner can render a stable row layout:
        // prefix text stays only on the first row, and separators are applied per row.
        const rows: TopBannerContentType['items'][] = [];
        for (let i = 0; i < items.length; i += 2) {
            rows.push(items.slice(i, i + 2));
        }
        return rows;
    }, [items]);

    const handleClose = useCallback(() => {
        hideBanner(cookieName, cookieTtlDays, setShowBanner);
    }, [cookieName, cookieTtlDays, setShowBanner]);

    if (!showBanner) {
        return null;
    }

    return (
        <div className="bg-banner-green flex min-h-20 w-full items-center justify-evenly px-6 text-sm font-black text-white md:px-[15%] md:text-lg">
            <div>
                {!hasItems && hasPrefixText ? <span>{prefixText}</span> : null}
                {itemRows.map((row, rowIndex) => (
                    <div key={`row-${rowIndex}`} className="flex flex-wrap items-center">
                        {rowIndex === 0 && hasPrefixText ? (
                            <span className="mr-1">{prefixText}</span>
                        ) : null}
                        {row.map((item, itemIndex) => (
                            <span key={`${rowIndex}-${itemIndex}`}>
                                {itemIndex > 0 ? <span className="mx-1">|</span> : null}
                                {item.url ? (
                                    <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="no-underline hover:underline"
                                    >
                                        {item.text}
                                    </a>
                                ) : (
                                    <span>{item.text}</span>
                                )}
                            </span>
                        ))}
                    </div>
                ))}
            </div>

            {cta ? (
                <Button
                    variant="outlineButton"
                    onClick={() => {
                        handleClose();
                        window.open(cta.url, '_blank', 'noopener,noreferrer');
                    }}
                    className="!py-2 px-4"
                >
                    {cta.text}
                </Button>
            ) : null}

            <button
                className="absolute right-6"
                onClick={handleClose}
                aria-label={gettext('CLOSE')}
            >
                <FontAwesomeIcon icon={faXmark} />
            </button>
        </div>
    );
}

export default TopBanner;
